import type {
	Enums,
	IncomingInvoiceDefaultApproverList,
	IncomingInvoiceWorkspace,
} from "@msuite/utils/types";

/** One set of default approvers as the settings table lists it: the organisation's or a workspace's. */
export interface InvoiceDefaultApproverSet {
	/** The workspace id, or `organization` for the organisation's set. */
	id: string;
	workspaceId: string | null;
	name: string;
	color: Enums<"color"> | null;
	approverNames: string[];
}

/**
 * The organisation's set first, then one per workspace in the order the workspaces come. A
 * workspace with no approvers of its own is listed too, with an empty set.
 *
 * @param organizationName - What the organisation's row is called.
 * @param workspaces - The organisation's workspaces.
 * @param approvers - Every default approver of the organisation.
 */
export function toInvoiceDefaultApproverSets(
	organizationName: string,
	workspaces: IncomingInvoiceWorkspace[],
	approvers: IncomingInvoiceDefaultApproverList,
): InvoiceDefaultApproverSet[] {
	const namesOf = (workspaceId: string | null) => {
		const names: string[] = [];
		for (const approver of approvers) {
			if (approver.workspaceId === workspaceId && approver.user.name)
				names.push(approver.user.name);
		}
		return names;
	};
	const sets: InvoiceDefaultApproverSet[] = [
		{
			id: "organization",
			workspaceId: null,
			name: organizationName,
			color: null,
			approverNames: namesOf(null),
		},
	];
	for (const workspace of workspaces) {
		sets.push({
			id: workspace.id,
			workspaceId: workspace.id,
			name: workspace.name,
			color: workspace.color,
			approverNames: namesOf(workspace.id),
		});
	}
	return sets;
}
